import { CURRENCIES, DEFAULT_CURRENCY, formatMoney } from '../../../../utils/currency';

// Cierre del lote del POS al cerrar la caja. El POS informa los totales del lote por moneda (código ISO numérico) y el
// sistema informa lo que la caja fue acumulando como cobrado con el POS: acá se arma lo que ve el cajero y el valor del campo "POS".

const PESOS_ISO = CURRENCIES[DEFAULT_CURRENCY].isoNumeric;
const codeOfIso = (iso) => Object.values(CURRENCIES).find((currency) => currency.isoNumeric === iso)?.code;
const TOLERANCE = 0.005;
const round2 = (value) => Math.round((Number(value) + Number.EPSILON) * 100) / 100;

// El lote quedó cerrado y aprobado: sus totales se pueden usar como lo declarado.
export const batchSucceeded = (result) =>
  ['CLOSED', 'RECOVERED', 'ALREADY_CLOSED', 'NOTHING_TO_CLOSE'].includes(result?.status) && result?.approved === true;

// Valores de los campos "POS": pesos en el formulario principal y cada otra moneda por su código.
export const posValuesFromBatch = (result) => {
  const net = (iso) => round2((result?.totals || []).find((total) => total.currency === iso)?.net ?? 0);
  const foreign = {};
  (result?.totals || []).forEach((total) => {
    const code = codeOfIso(total.currency);
    if (code && code !== DEFAULT_CURRENCY) foreign[code] = round2(total.net);
  });
  return { pesos: net(PESOS_ISO), foreign };
};

// Diferencias entre lo que dice el POS y lo que la caja tenía anotado, por moneda (solo las que no coinciden).
export const batchDifferences = (result) =>
  (result?.totals || [])
    .map((total) => {
      const system = Number((result.system_pos || {})[total.currency] ?? 0);
      return { code: codeOfIso(total.currency) || total.currency, pos: round2(total.net), system: round2(system), difference: round2(total.net - system) };
    })
    .filter((entry) => Math.abs(entry.difference) > TOLERANCE);

// Mensaje para el cajero según el resultado del cierre del lote.
export const batchMessage = (result) => {
  const status = result?.status;
  if (status === 'NOTHING_TO_CLOSE') {
    return { severity: 'info', title: 'No había nada para cerrar en el POS', message: 'No hubo cobros nuevos desde el último cierre de lote.' };
  }
  if (status === 'PENDING') {
    return { severity: 'warning', title: 'El POS no contestó a tiempo', message: 'No sabemos si el lote se cerró. Podés reintentar o cerrar la caja igual: el lote queda pendiente y se cierra en el próximo cierre.' };
  }
  if (status === 'ERROR') {
    return { severity: 'error', title: 'No se pudo cerrar el lote en el POS', message: `${result.message || 'Error al comunicarse con el POS.'} Podés reintentar o cerrar la caja igual: el lote queda pendiente.` };
  }
  if (status === 'CLOSED' || status === 'RECOVERED' || status === 'ALREADY_CLOSED') {
    if (result.approved !== true) {
      return { severity: 'warning', title: 'El procesador no aprobó el cierre del lote', message: `${result.message || ''} Podés reintentar o cerrar la caja igual: el lote queda pendiente.`.trim() };
    }
    const { pesos } = posValuesFromBatch(result);
    const differences = batchDifferences(result);
    const lot = result.lot ? `Lote ${result.lot}` : 'Lote';
    const recovered = status === 'RECOVERED' ? ' (el cierre ya se había hecho y se leyeron sus totales)' : status === 'ALREADY_CLOSED' ? ' (ya estaba cerrado)' : '';
    if (differences.length === 0) {
      return { severity: 'success', title: `${lot} cerrado en el POS${recovered}`, message: `Total del POS: ${formatMoney(pesos, DEFAULT_CURRENCY)}. Coincide con lo registrado en el sistema.` };
    }
    const detail = differences
      .map((entry) => `${entry.code}: el POS informa ${formatMoney(entry.pos, entry.code)} y el sistema registró ${formatMoney(entry.system, entry.code)} (diferencia ${formatMoney(entry.difference, entry.code)})`)
      .join('. ');
    return { severity: 'warning', title: `${lot} cerrado en el POS${recovered}: hay una diferencia`, message: `${detail}. Revisá si hubo ventas con tarjeta fuera del POS o devoluciones hechas en el POS.` };
  }
  return null;
};
